import { Button, Icon, Modal, notification, Tooltip } from 'antd'
import 'primeflex/primeflex.css'
import 'primeicons/primeicons.css'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import 'primereact/resources/primereact.css'
import 'primereact/resources/themes/lara-light-indigo/theme.css'
import { Toast } from 'primereact/toast'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { api, request } from '../../../util/handlerRequestUtil'
import { getMessage } from '../../../util/IntlMessages'
import { isGR, isRtv } from '../../../util/profiles'
import AddAlternativeToGroup from './Alternative/AddAlternativeToGroup'
import ListAlternatives from './Alternative/Alternatives.List'
import CreateGroup from './CreateGroup'
import Header from './Header'
import { listCustomerGroups } from './listCustomerGroups'

const ListGroups = ({ customerId, groups, setGroups }) => {
  const [groupName, setGroupName] = useState('')
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedGroupId, setSelectedGroupId] = useState(null)
  const [locationDistrictMap, setLocationDistrictMap] = useState({})
  const [customerLocations, setCustomerLocations] = useState([])
  const [districts, setDistricts] = useState([])
  const [modalDocumentVisible, setModalDocumentVisible] = useState(false)
  const [modalGroupVisible, setModalGroupVisible] = useState(false)
  const [expandedRows, setExpandedRows] = useState([])
  const toast = useRef(null)
  const currentUser = useSelector((state) => state.auth.auth)

  useEffect(() => {
    listGroupsByCustomerId(customerId)
  }, [customerId, modalGroupVisible, modalDocumentVisible, groupName])

  useEffect(() => {
    loadModalDependencies(customerId)
  }, [customerId, currentUser?.profileId])

  async function listGroupsByCustomerId(customerId) {
    setExpandedRows([])
    const groups = await listCustomerGroups(customerId)
    setGroups(groups)
  }

  async function loadModalDependencies(customerId) {
    const { regions: memberRegions, districts: memberDistricts } = currentUser
    let params = {}

    if (isRtv()) {
      params.districtId = memberDistricts.map(({ id }) => id)
    } else if (isGR()) {
      params.regionId = memberRegions.map(({ id }) => id)
    }

    const locations = await request(
      api.listCustomerLocations(customerId, { params: { active: true, ...params } }),
      false,
      true
    )
    setCustomerLocations(locations || [])

    const districtByLocation = (locations || []).reduce((acc, location) => {
      acc[location.id] =
        location.districtName ||
        location?.district?.districtName ||
        location?.district?.name ||
        `Distrito ${location.districtId}`
      return acc
    }, {})

    setLocationDistrictMap(districtByLocation)

    const districtOptions = [
      ...new Map(
        (locations || [])
          .filter((location) => !!location.districtId)
          .map((location) => [
            +location.districtId,
            {
              value: +location.districtId,
              label:
                location.districtName ||
                location?.district?.districtName ||
                location?.district?.name ||
                `Distrito ${location.districtId}`
            }
          ])
      ).values()
    ]
    setDistricts(districtOptions)
  }

  async function removeGroup(groupId) {
    const alternativesCount = await request(api.countAlternativeDocumentsByGroupId(groupId), false, true)
    if (alternativesCount > 0) {
      notification.error({
        description: 'Antes de remover o grupo é necessário remover os alternativos',
        message: getMessage('sweetAlerts.warning')
      })
    } else {
      await request(api.removeCustomerGroup(groupId))
      notification.warning({
        description: 'Grupo removido com sucesso!',
        message: getMessage('sweetAlerts.warning')
      })
    }
    await listGroupsByCustomerId(customerId)
  }

  const actionsTemplate = (group) => {
    const { id, description } = group
    return (
      <React.Fragment>
        <div style={{ display: 'flex', gap: '5px' }}>
          <Button
            type={'ghost'}
            onClick={() => {
              setModalDocumentVisible(true)
              setSelectedGroupId(id)
              setSelectedGroup(group)
            }}
          >
            <Icon type="plus" size="small" />
            {getMessage('createAlternativeLabelButton')}
          </Button>
          <Tooltip title={'Editar o grupo'}>
            <Button
              onClick={() => {
                setModalGroupVisible(true)
                setSelectedGroupId(id)
                setGroupName(description)
                setSelectedGroup(group)
              }}
            >
              <Icon type="edit" size="small" />
            </Button>
          </Tooltip>
          <Tooltip title={'Deletar o grupo'}>
            <Button onClick={() => removeGroup(id)}>
              <Icon type="delete" size="small" />
            </Button>
          </Tooltip>
        </div>
      </React.Fragment>
    )
  }

  const onRowGroupExpand = ({ data }) => {
    toast.current.show({
      severity: 'info',
      summary: 'Expandido',
      detail: 'Grupo: ' + data.description,
      life: 3000
    })
  }

  const onRowGroupCollapse = ({ data }) => {
    toast.current.show({
      severity: 'success',
      summary: 'Fechando',
      detail: 'Grupo: ' + data.description,
      life: 3000
    })
  }

  const allowExpansion = (rowData) => {
    return rowData.documents.length > 0
  }

  const rowExpansionTemplate = ({ id }) => {
    return <ListAlternatives groupId={id} />
  }

  const districtsTemplate = (group) => {
    const districtNames = [
      ...new Set(
        (group?.locations || [])
          .map((item) => item?.locationId)
          .filter(Boolean)
          .map((locationId) => locationDistrictMap[locationId])
          .filter(Boolean)
      )
    ]

    if (districtNames.length === 0) return '-'
    return districtNames.join(', ')
  }

  const updateGroupsList = useCallback((newGroup) => {
    setGroups((previousList) => [...previousList, newGroup])
  }, [])

  const handleGroupCreated = useCallback((newGroup) => {
    setSelectedGroupId(newGroup.id)
    setSelectedGroup(newGroup)
    setModalDocumentVisible(true)
  }, [])

  return (
    <>
      <Header
        customerId={customerId}
        updateGroupsList={updateGroupsList}
        customerLocations={customerLocations}
        districts={districts}
        onGroupCreated={handleGroupCreated}
      />
      <div>
        <Toast ref={toast}></Toast>
        <DataTable
          value={groups}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          onRowExpand={onRowGroupExpand}
          onRowCollapse={onRowGroupCollapse}
          rowExpansionTemplate={rowExpansionTemplate}
          dataKey="id"
          tableStyle={{ minWidth: '100%' }}
          responsiveLayout="scroll"
        >
          <Column expander={allowExpansion} style={{ width: '5%' }} />
          <Column field="description" header="Nome do grupo" sortable style={{ width: '45%' }} />
          <Column header="Distritos" body={districtsTemplate} style={{ width: '45%' }} />
          <Column header="Ações" body={actionsTemplate}></Column>
        </DataTable>
      </div>
      <Modal
        destroyOnClose={() => {
          setModalGroupVisible(false)
          setGroupName('')
          setSelectedGroup(null)
        }}
        onCancel={() => {
          setModalGroupVisible(false)
          setGroupName('')
          setSelectedGroup(null)
        }}
        title={
          <h3 style={{ fontWeight: 'bold' }}>
            {selectedGroupId ? getMessage('updateGroup') : getMessage('createGroup')}
          </h3>
        }
        footer={null}
        visible={modalGroupVisible}
      >
        <CreateGroup
          groupId={selectedGroupId}
          setModalGroupVisible={setModalGroupVisible}
          customerId={customerId}
          currentGroupName={groupName}
          currentGroup={selectedGroup}
          customerLocations={customerLocations}
          districts={districts}
        />
      </Modal>
      <Modal
        destroyOnClose={() => setModalDocumentVisible(false)}
        onCancel={() => setModalDocumentVisible(false)}
        title={<h3 style={{ fontWeight: 'bold' }}>{getMessage('createAlternate')}</h3>}
        footer={null}
        width="70%"
        visible={modalDocumentVisible}
      >
        <AddAlternativeToGroup groupId={selectedGroupId} setModalDocumentVisible={setModalDocumentVisible} />
      </Modal>
    </>
  )
}

export default ListGroups
